import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Pencil, Trash2, Plus, Loader2, Library, GripVertical, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CollectionIcon, IconGallery, collectionLink } from './Collections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Não foi possível salvar.');

const CollectionForm = ({ initial, onSave, onCancel, busy }) => {
  const [nome, setNome] = useState(initial?.nome || '');
  const [icon, setIcon] = useState(initial?.icon || 'library');
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ nome, icon }); }} data-testid="collection-form"
      style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1rem', marginBottom: '1rem', display: 'grid', gap: '0.75rem' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <span style={{ width: 42, height: 42, borderRadius: 10, background: '#e6f6f7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2c7a7b' }}>
          <CollectionIcon name={icon} className="w-6 h-6" />
        </span>
        <Input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={40} placeholder="Nome da coleção" data-testid="collection-name-input" required />
      </div>
      <p style={{ fontSize: '0.8rem', color: '#4a5568' }}>Escolha um símbolo:</p>
      <IconGallery value={icon} onChange={setIcon} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="collection-cancel-btn">Cancelar</Button>
        <Button type="submit" disabled={busy} style={{ background: '#68AAB0' }} data-testid="collection-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}</Button>
      </div>
    </form>
  );
};

const CopyLinkBtn = ({ baseUrl, nome, testId, empty }) => (
  <Button variant="ghost" size="sm" title={empty ? 'Adicione livros a esta coleção para divulgar o link' : 'Copiar link da coleção'} data-testid={testId} disabled={empty}
    onClick={() => navigator.clipboard.writeText(collectionLink(baseUrl, nome)).then(() => toast.success('Link copiado', { description: collectionLink(baseUrl, nome) })).catch(() => toast.error('Não foi possível copiar'))}>
    <Link2 className="w-4 h-4" />
  </Button>
);

export const CollectionsManager = ({ headers, onChanged, baseUrl }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/digital/collections`, { headers }).then(({ data: d }) => { setData(d); setOrder(null); }).catch(() => toast.error('Erro ao carregar coleções'));
  useEffect(() => { load(); }, []);
  const save = async (body) => {
    setBusy(true);
    try {
      if (editing === 'new') await axios.post(`${API}/digital/collections`, body, { headers });
      else await axios.put(`${API}/digital/collections/${editing.id}`, body, { headers });
      toast.success('Coleção salva');
      setEditing(null); load(); onChanged?.();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Excluir a coleção "${c.nome}"? Os livros não são apagados; os que ficarem sem coleção passam para "Geral".`)) return;
    try { await axios.delete(`${API}/digital/collections/${c.id}`, { headers }); toast.success('Coleção excluída'); load(); onChanged?.(); } catch (e) { toast.error(errText(e)); }
  };
  const [order, setOrder] = useState(null);
  const [dragId, setDragId] = useState(null);
  const cols = order || data?.collections || [];
  const dirty = !!order && order.some((c, i) => c.id !== data.collections[i]?.id);
  const moveTo = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const list = [...cols];
    const from = list.findIndex((c) => c.id === dragId);
    const [item] = list.splice(from, 1);
    list.splice(list.findIndex((c) => c.id === targetId), 0, item);
    setOrder(list);
  };
  const saveOrder = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/digital/collections/order`, { ids: cols.map((c) => c.id) }, { headers });
      toast.success('Ordem salva');
      setOrder(null); await load(); onChanged?.();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  if (!data) return <Loader2 className="w-6 h-6 animate-spin" />;
  const full = data.collections.length >= data.max;
  return (
    <div data-testid="collections-manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2D3748' }}>Coleções</h2>
          <p data-testid="collections-count" style={{ color: '#718096', fontSize: '0.9rem' }}>{data.collections.length} de {data.max} coleções · livros sem coleção ficam em "Geral"</p>
        </div>
        <Button onClick={() => setEditing('new')} disabled={full || editing === 'new'} style={{ background: '#68AAB0' }} data-testid="collection-new-btn"><Plus className="w-4 h-4 mr-1" />Nova coleção</Button>
      </div>
      {editing === 'new' && <CollectionForm onSave={save} onCancel={() => setEditing(null)} busy={busy} />}
      {cols.length > 1 && (
        <div data-testid="collections-order-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10, fontSize: '0.85rem', color: '#4a5568' }}>
          <span>Arraste as coleções pelo <GripVertical className="w-4 h-4 inline" /> para definir a ordem da faixa do catálogo ("Geral" fica sempre no início).</span>
          {dirty && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outline" size="sm" onClick={() => setOrder(null)} data-testid="collections-order-cancel-btn">Desfazer</Button>
              <Button size="sm" onClick={saveOrder} disabled={busy} style={{ background: '#68AAB0' }} data-testid="collections-order-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar ordem'}</Button>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'grid', gap: 10 }}>
        <div data-testid="collection-row-geral" style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f7fafc', border: '1px dashed #cbd5e0', borderRadius: 12, padding: '0.75rem 1rem' }}>
          <Library className="w-5 h-5" strokeWidth={1.5} /><strong style={{ flex: 1 }}>Geral</strong>
          <span style={{ fontSize: '0.85rem', color: '#718096' }}>{data.geral_books} livro(s) · automática</span>
          <CopyLinkBtn baseUrl={baseUrl} nome="Geral" testId="collection-copy-link-geral" empty={!data.geral_books} />
        </div>
        {cols.map((c) => (editing?.id === c.id ? <CollectionForm key={c.id} initial={c} onSave={save} onCancel={() => setEditing(null)} busy={busy} /> : (
          <div key={c.id} data-testid={`collection-row-${c.id}`} draggable={!editing}
            onDragStart={(e) => { setDragId(c.id); e.dataTransfer.effectAllowed = 'move'; }}
            onDragOver={(e) => { e.preventDefault(); moveTo(c.id); }}
            onDragEnd={() => setDragId(null)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'white', border: `1px solid ${dragId === c.id ? '#68AAB0' : '#e2e8f0'}`, borderRadius: 12, padding: '0.75rem 1rem', opacity: dragId === c.id ? 0.6 : 1, cursor: editing ? 'default' : 'grab', transition: 'opacity 0.15s ease, border-color 0.15s ease' }}>
            <GripVertical className="w-4 h-4" style={{ color: '#a0aec0' }} data-testid={`collection-drag-${c.id}`} />
            <CollectionIcon name={c.icon} className="w-5 h-5" /><strong style={{ flex: 1 }}>{c.nome}</strong>
            <span style={{ fontSize: '0.85rem', color: '#718096' }}>{c.books} livro(s)</span>
            <CopyLinkBtn baseUrl={baseUrl} nome={c.nome} testId={`collection-copy-link-${c.id}`} empty={!c.books} />
            <Button variant="ghost" size="sm" onClick={() => setEditing(c)} title="Editar" data-testid={`collection-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => remove(c)} title="Excluir" style={{ color: '#e53e3e' }} data-testid={`collection-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        )))}
      </div>
    </div>
  );
};
